import { colors } from '@smartsheet/lodestar-core';
import React, { ReactElement, useEffect, useState } from 'react';
import ReactFlow, { Edge, Node } from 'react-flow-renderer';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  RIGHT_RAIL_TABS,
  selectedElementSelector,
  selectGrabbedItem,
  setActiveTab,
  setSelectedElementID,
  GraphNodeTypes,
} from '../../../services/ducks/designerDuck/designerDuck';
import {
  graphSelector,
  nodesSelector,
  startNodeSelector,
  WorkflowState,
  WorkflowStateNode,
} from '../../../services/ducks/workflowDuck/workflowDuck';
import {
  HORIZONTAL_SPACING,
  NODE_HEIGHT,
  NODE_WIDTH,
  VERTICAL_SPACING,
} from '../../../utils/dimensions';
import {
  ChildWorkflow,
  Junction,
  Module,
  State,
  Trigger,
  DropTarget,
  GroupExit,
} from './Nodes';
import { Edge as CustomEdge } from './canvas-elements/edges/Edge';
import {
  canAddChildWorkflow,
  canAddGroup,
  canAddJunction,
  canAddModule,
  canAddState,
  isCurrentNodeOrChildToCurrentNode,
} from './utils';
interface CanvasProps {
  workflowData: any;
}

export const dropLocationsRules: {
  [key in GraphNodeTypes]: (
    nodeId: string,
    graphitems: {},
    grabbedItemId: WorkflowStateNode['id']
  ) => boolean;
} = {
  [GraphNodeTypes.STATE]: canAddState,
  [GraphNodeTypes.MODULE]: canAddModule,
  [GraphNodeTypes.JUNCTION]: canAddJunction,
  [GraphNodeTypes.GROUP_JUNCTION]: canAddGroup,
  [GraphNodeTypes.GROUP_EXIT]: () => false,
  [GraphNodeTypes.CHILD]: canAddChildWorkflow,
  [GraphNodeTypes.TRIGGER]: () => false,
};
export const Canvas = ({ workflowData }: CanvasProps) => {
  type ReactFlowNodeData = {
    label: ReactElement;
    data: WorkflowState['data']['graph']['items'];
  };

  const selectedElement = useSelector(selectedElementSelector);
  const grabbedItem = useSelector(selectGrabbedItem);
  const [renderItems, setRenderItems] = useState<any>([]);
  const graph = useSelector(graphSelector);
  const startNodeID = useSelector(startNodeSelector);
  const nodesInGraph = useSelector(nodesSelector);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  useEffect(() => {
    // Build render items
    const ids = (graph.items && Object.keys(graph.items)) || [];
    const nodes: Node[] = [];
    const dropTargets: Node[] = [];
    const layers: number[] = [];
    const modifier = 1; // isDragging ? 2 : 1;

    ids.forEach((id: string) => {
      const item = graph.items[id];
      const x = item.pos[0] * NODE_WIDTH + item.pos[0] * HORIZONTAL_SPACING;
      const y =
        item.pos[1] * modifier * NODE_HEIGHT +
        item.pos[1] * modifier * VERTICAL_SPACING;

      if (grabbedItem !== undefined) {
        const isValidDropLocation = dropLocationsRules[grabbedItem.type];
        if (
          !isCurrentNodeOrChildToCurrentNode(id, grabbedItem.id, graph.items) &&
          isValidDropLocation(id, graph.items, grabbedItem.id)
        ) {
          dropTargets.push({
            id: `${id}-dropTarget`,
            data: { prevId: id },
            type: 'DropTarget',
            position: { x, y: y + (VERTICAL_SPACING + NODE_HEIGHT) / 2 },
          });
          layers.push(graph.layerMap[id]);
        }
      }

      const node: Node<ReactFlowNodeData> = {
        id,
        type: item.type,
        data: {
          label: (
            <div>
              {nodesInGraph[id]?.type === GraphNodeTypes.GROUP_EXIT
                ? nodesInGraph[id]?.exit
                : nodesInGraph[id]?.state}
            </div>
          ),
          data: nodesInGraph[id],
        },
        position: { x, y },
      };
      nodes.push(node);
    });

    const edges: Edge[] = [];
    const highlightedEdges: Edge[] = [];
    graph.edges &&
      graph.edges.map((edge: any) => {
        const id = `${edge.prevId}-${edge.nextId}`;
        const renderEdge = {
          id,
          source: `${edge.prevId}`,
          target: `${edge.nextId}`,
          type: 'custom',
          data: {
            animated: false,
            highlights:
              selectedElement && Object.keys(selectedElement).length
                ? true
                : false,
            highlighted: false,
            ...edge,
          },
        };

        if (selectedElement && id.includes(selectedElement.id)) {
          if (id.includes(`${selectedElement.id}-`)) {
            renderEdge.data.outgoingEdge = true;
          }
          renderEdge.data.animated = false;
          renderEdge.data.highlighted = true;
          highlightedEdges.push(renderEdge);
        } else {
          edges.push(renderEdge);
        }
      });

    setRenderItems([...nodes, ...dropTargets, ...edges, ...highlightedEdges]);
  }, [
    workflowData.transits,
    workflowData.startState,
    grabbedItem,
    graph,
    selectedElement,
    startNodeID,
    nodesInGraph,
    translate,
  ]);

  const nodeTypes = {
    [GraphNodeTypes.CHILD]: ChildWorkflow,
    [GraphNodeTypes.JUNCTION]: Junction,
    [GraphNodeTypes.MODULE]: Module,
    [GraphNodeTypes.STATE]: State,
    [GraphNodeTypes.TRIGGER]: Trigger,
    DropTarget,
    [GraphNodeTypes.GROUP_JUNCTION]: Module, // needs new component
    [GraphNodeTypes.GROUP_EXIT]: GroupExit,
  };

  const onLoad = (reactFlowInstance: any) => {
    reactFlowInstance.setTransform({
      x: window.innerWidth / 2 - NODE_WIDTH,
      y: 100,
      zoom: 1,
    });
    reactFlowInstance.fitView();
  };

  return (
    // id="canvas" used in panel component in handling mousedown events to close the panel if the event occured outside the panel
    (<div
      style={{ width: '100%', height: 'calc(100vh - 56px)', paddingTop: 56 }}
      id="canvas"
    >
      <ReactFlow
        onLoad={onLoad}
        elements={renderItems}
        nodesConnectable={false}
        nodesDraggable={false}
        nodeTypes={nodeTypes}
        edgeTypes={{ custom: CustomEdge }}
        style={{ background: colors.defaultBackground }}
        elementsSelectable={true}
        onElementClick={(node, e) => {
          dispatch(setSelectedElementID(e.id));
          dispatch(setActiveTab(RIGHT_RAIL_TABS.EDIT_MODULE));
        }}
      />
    </div>)
  );
};
